import React, { ReactElement, ReactNode } from 'react';
import { secondaryButtonColorStyle } from '../../../../atoms/buttons/SecondaryButton';
import styled from '@emotion/styled';
import IconCross from '../../../../atoms/icons/IconCross';
import typography from '../../../../theme/typography';
import Button from '../../../../atoms/buttons/Button';
import { white } from '../../../../theme/colors';
import borders from '../../../../theme/borders';
import { isDarkMode } from '../../../../utils/helpers';
import BasicModal from '../../../modals/BasicModal';
import backgrounds from '../../../../theme/backgrounds';
import useMediaBreakpoints from '../../../../hooks/useMediaBreakpoints';
import { useMediaQuery, useTheme } from '@material-ui/core';
import breakpoints from 'theme/material/breakpoints';

const cancelButtonStyle = secondaryButtonColorStyle({ color: 'red' });

const StyledCrossIcon = styled(IconCross)((): any => {
  const theme = isDarkMode() ? 'dark' : 'light';

  return {
    height: '13px !important',
    width: '13px !important',
    position: 'relative',
    left: '-4px',
    top: '1px',
    fill: cancelButtonStyle[theme].color,

    [breakpoints.down(639.05)]: {
      width: '20px !important',
      height: '20px !important',
    },
  };
});

const StepDescription = styled('span')(() => {
  const theme = isDarkMode() ? 'dark' : 'light';

  return {
    fontSize: 14,
    color: typography[theme].primaryText,
    display: 'inline-block',
    fontWeight: 'normal',
    marginLeft: 10,
    letterSpacing: -0.42,
  };
});

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const CancelButton = styled(Button)((): any => {
  const theme = isDarkMode() ? 'dark' : 'light';

  return {
    fontSize: '13px',
    fontWeight: 600,
    color: cancelButtonStyle[theme].color,
    borderRadius: 6,
    border: `1px solid ${cancelButtonStyle[theme].borderColor}`,
    padding: '8px 18px',
    display: 'flex',
    alignContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    lineHeight: '17px',
    marginTop: '20px',
    height: '32px',
    width: '99px',
    placeContent: 'center',

    [breakpoints.up(769)]: {
      '&:hover': {
        color: `${cancelButtonStyle[theme].hover.color} !important`,
        border: `1px solid ${cancelButtonStyle[theme].hover.borderColor} !important`,
        backgroundColor: `${cancelButtonStyle[theme].hover.backgroundColor} !important`,
        '& svg path': {
          fill: `${cancelButtonStyle[theme].hover.color} !important`,
        },
      },
      '&:disabled': {
        color: `${cancelButtonStyle[theme].disabled.color} !important`,
        border: `1px solid ${cancelButtonStyle[theme].disabled.borderColor} !important`,
        backgroundColor: `${cancelButtonStyle[theme].disabled.backgroundColor} !important`,
      },
      '&:active': {
        color: `${cancelButtonStyle[theme].active.color} !important`,
        border: `1px solid ${cancelButtonStyle[theme].active.borderColor} !important`,
        backgroundColor: cancelButtonStyle[theme].active.backgroundColor,
        '& svg path': {
          fill: `${cancelButtonStyle[theme].active.color} !important`,
        },
      },
      '&:focus': {
        color: `${cancelButtonStyle[theme].hover.color} !important`,
        backgroundColor: `${cancelButtonStyle[theme].hover.backgroundColor} !important`,
        border: `1px solid ${cancelButtonStyle[theme].hover.borderColor} !important`,
        '& svg path': {
          fill: `${cancelButtonStyle[theme].hover.color} !important`,
        },
      },
    },

    [breakpoints.down(639.05)]: {
      border: 'none',
      padding: '5px',
      marginRight: '-10px',
      marginTop: '14px',
      width: 'inherit',
      '&:active': {
        backgroundColor: 'none !important',
        border: 'none !important',
      },
    },
  };
});

const CancelButtonText = styled('span')({
  fontSize: 13,
  lineHeight: typography.lineHeight,
  [breakpoints.down(479.05)]: {
    display: 'none',
  },
});

const HeaderContainer = styled('div')(() => {
  const theme = isDarkMode() ? 'dark' : 'light';

  return {
    display: 'flex',
    alignContent: 'center',
    alignItems: 'center',
    marginTop: 6,
    marginRight: 2,
    maxHeight: 48,

    [breakpoints.down(479.05)]: {
      borderBottom: `1px solid ${borders[theme].primary}`,
    },
  };
});

const HeaderText = styled('div')({
  flex: 10,
});

const Title = styled('h2')(() => {
  const theme = isDarkMode() ? 'dark' : 'light';

  return {
    borderBottom: `1px solid ${borders[theme].primary}`,
    fontSize: 20,
    color: typography[theme].heading,
    marginRight: 12,
    marginTop: 40,
    paddingBottom: 4,
    lineHeight: '24.2px',

    [breakpoints.down(479.05)]: {
      borderBottom: 'none',
    },
  };
});

interface GalleryUploaderModalProps {
  isOpen: boolean;
  title: string;
  children: ReactNode;
  contentLabel?: string;
  shouldCloseOnOverlayClick?: boolean;
  className?: string;
  description?: string;
  onClose: () => void;
}

const GalleryUploaderModal = ({
  description,
  title,
  isOpen,
  shouldCloseOnOverlayClick = false,
  onClose,
  className,
  children,
}: GalleryUploaderModalProps): ReactElement => {
  const theme = useTheme();
  const { isMobile, isTablet } = useMediaBreakpoints();
  const tabletWidth = useMediaQuery(theme.breakpoints.between(712, 1024));

  const getCustomStyle = () => {
    return {
      overlay: {
        zIndex: 10001,
      },
      content: {
        padding: `5px ${isMobile ? '15px' : '20px'}`,
        margin: isMobile && !tabletWidth ? '0px auto' : '25px auto',
        overflow: isMobile && !tabletWidth ? 'scroll' : 'unset',
        top: isMobile && !tabletWidth ? 0 : 25,
        height: isMobile && !tabletWidth ? '100%' : 'auto',
        borderRadius: 6,
        maxWidth: isTablet ? 630 : 685,
        backgroundColor: !isDarkMode() ? white : backgrounds.dark.primaryCard,
        border: `1px solid ${!isDarkMode() ? borders.light.primary : borders.dark.primary}`,
      },
    };
  };

  return (
    <BasicModal
      hideClose={true}
      ariaHideApp={false}
      className={`${className} wyp-bulk-photo-popup`}
      isOpen={isOpen}
      customStyle={getCustomStyle()}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      onClose={onClose}
      data={{ 'test-id': 'bulk-upload-photo-modal' }}
    >
      <HeaderContainer>
        <HeaderText>
          <Title>
            {title}
            {description && <StepDescription>{description}</StepDescription>}
          </Title>
        </HeaderText>
        <div className="top-actions">
          <CancelButton onClick={onClose} isDefault={true}>
            <StyledCrossIcon />
            <CancelButtonText>Cancel</CancelButtonText>
          </CancelButton>
        </div>
      </HeaderContainer>
      {children}
    </BasicModal>
  );
};

export default GalleryUploaderModal;
